import React, { useState, useEffect } from 'react';
import { 
  Button, 
  IconButton, 
  Tooltip, 
  Tabs, 
  Tab, 
  Box, 
  Chip, 
  Avatar, 
  List, 
  ListItem, 
  ListItemAvatar, 
  TextField,
  InputAdornment,
  Typography,
  Divider
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { Redirect, useLocation } from 'react-router-dom';
import ContactForm from '../forms/ContactForm_v2/ContactForm_v2';
import ConDetails from '../../container/Contacts/ConDetails'
import moment from 'moment';
import axios from 'axios';
import { APIURL } from '../../config'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { SEGMENTAPIKEY } from '../../config';

const analytics = AnalyticsBrowser.load({ writeKey: SEGMENTAPIKEY })
const RECENT_CONTACT_PERIOD = { amount: 1, unit: 'month' };
const FAMILY_CONTACT_TYPES = ['Family', 'Spouse', 'Brother', 'Friend'];
const CARE_CONTACT_TYPES = ['Legal Guardian', 'Care Giver', 'Service Provider'];

export default function Contacts(props) {
  const location = useLocation();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [redirectToNew, setRedirectToNew] = useState(false);
  const [loggedToSegment, setLoggedToSegment] = useState(false);
  const [customerList, setCustomerList] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const dateComparator = (v1, v2) => moment(v1).diff(moment(v2));

  const logToSegment = async () => {
    if (loggedToSegment) {
      return;
    }
    let page = String(window.location.href).split("/").pop()
    if (page.includes("fieldworker") || page === "" || page === "#") {
      page = "Home";
    } else {
      page = String(page).charAt(0).toUpperCase() + String(page).toLowerCase().slice(1);
    }
    console.log(page, props.user.emp_id)
    analytics.page(page, {
      title: page,
      path: page,
    }, {
      userId: String(props.user.emp_id),
    })
    console.log("logged to segment successfully")
    setLoggedToSegment(true);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    props.checkTokenExpiry()
    logToSegment();

    axios
      .get(APIURL + "/contacts", {
        params: { com_id: props.comp_id },
      })
      .then((response) => {
        const data = response.data;
        setContacts(data);
        const navConId = location?.contactsProps?.con_id;
        const preselect = navConId ? data.find(c => c.con_id === navConId || c.id === navConId) : null;
        setSelectedContact(preselect || data[data.length - 1]);
      })
      .catch((err) => {
        console.log(err);
      });

    axios
      .get(APIURL + "/customers/v2/associated", {
        params: {
          company_id: props.user.comp_id,
          employee_id: props.user.emp_id,
          type: props.user.permissions.AdminLevelCustomers ? "Admin": "Manager"
        },
      })
      .then((response) => {
        setCustomerList(response?.data.filter((cus) => cus.cus_status === 'Active') || []);
      });

    setIsLoading(false);
  };

  const onSelect = (id) => {
    const contactSelection = contacts.find((con) => (con.con_id === id));

    setSelectedContact(contactSelection);
    setIsEdit(false);
  };

  const onDel = async (id) => {
    axios
      .delete(APIURL + "/contacts", {
        data: { id: id, bval: false }
      })
      .then((response) => {
        console.log(response);
        fetchData();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onEdit = () => {
    setIsEdit(true);
  };

  const updateContact = (basic, other) => {
    let updatedContact = contacts.map((con) => {
      if (con.con_id === selectedContact.con_id) {
        con = { ...con, con_first_name: basic.first_name, con_last_name: basic.last_name, con_Addr_line1: basic.address_line_1, con_Addr_line2: basic.address_line_2, con_city: basic.city, con_state: basic.state, con_zip: basic.zip, con_phone: basic.phnNumList.map((number) => { return number.country_code + number.phone }).join(), con_designation: other.designation, con_status: other.status, con_contact_type: other.contact_type, con_first_contact_date: other.first_contact_date, con_last_contact_date: other.last_contact_date }
      }
      return con
    })

    setContacts(updatedContact);
    setIsEdit(false); // Close edit form after update
    // refresh full list to include latest edits
    fetchData();
  };

  const getBadgeColor = (status) => {
    const statusMap = {
      'Active': 'success',
      'Lead': 'warning',
      'Professional': 'info',
      'Vendor': 'default',
      'Client': 'primary',
    };
    return statusMap[status] || 'default';
  };

  const getTypeColor = (type) => {
    const typeMap = {
      'Client': '#1976d2',
      'Professional': '#7B68EE',
      'Vendor': '#808080',
      'Friend': '#20B2AA',
      'Lead': '#FFA500',
    };
    return typeMap[type] || '#757575';
  };

  const filterContacts = () => {
    let filtered = [...contacts];
    const getContactActivityDate = (contact) => moment(contact.con_last_contact_date || contact.con_updated_at);

    // Filter by tab
    switch (activeTab) {
      case 1: { // Recent
        const recentCutoff = moment().subtract(RECENT_CONTACT_PERIOD.amount, RECENT_CONTACT_PERIOD.unit);
        filtered = filtered
          .filter(con => getContactActivityDate(con).isSameOrAfter(recentCutoff, 'day'))
          .sort((a, b) => getContactActivityDate(b).diff(getContactActivityDate(a)));
        break;
      }
      case 2: // Patient
        filtered = filtered.filter(con => con.con_contact_type === 'Patient');
        break;
      case 3: // Family
        filtered = filtered.filter(con => FAMILY_CONTACT_TYPES.includes(con.con_contact_type));
        break;
      case 4: // Care
        filtered = filtered.filter(con => CARE_CONTACT_TYPES.includes(con.con_contact_type));
        break;
      default: // All
        break;
    }

    // Filter by search
    if (searchQuery) {
      filtered = filtered.filter(con => 
        `${con.con_first_name} ${con.con_last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (con.con_designation && con.con_designation.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    return filtered;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCall = () => {
    // TODO: Implement call functionality
    if (selectedContact?.con_phone) {
      window.location.href = `tel:${selectedContact.con_phone.split(',')[0]}`;
    }
  };

  const handleEmail = () => {
    // TODO: Implement email functionality
    if (selectedContact?.con_email) {
      window.location.href = `mailto:${selectedContact.con_email}`;
    }
  };

  const filteredContacts = filterContacts();
  const hasSelectedContactPhone = Boolean(selectedContact?.con_phone?.trim());
  const hasSelectedContactEmail = Boolean(selectedContact?.con_email?.trim());

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 100px)', width: '100%', backgroundColor: '#fafafa' }}>
      {/* LEFT PANE - Contact List */}
      <div style={{ 
        width: '35%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRight: '1px solid #e0e0e0',
        backgroundColor: 'white'
      }}>
        {/* Header with Search */}
        <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Contacts</Typography>
            <Button
              variant="contained"
              size="small"
              disabled={props.user.type === 'Fieldworker' || props.user.type === 'Support Coordinator'}
              onClick={(e) => { setRedirectToNew(true); props.changeScreen('new', 'con'); }}
              startIcon={<AddIcon />}
            >
              New
            </Button>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Tabs */}
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: '1px solid #e0e0e0', minHeight: 40 }}
        >
          <Tab label="All" sx={{ minHeight: 40, py: 0.75, textTransform: 'none' }} />
          <Tab label="Recent" sx={{ minHeight: 40, py: 0.75, textTransform: 'none' }} />
          <Tab label="Patients" sx={{ minHeight: 40, py: 0.75, textTransform: 'none' }} />
          <Tab label="Family" sx={{ minHeight: 40, py: 0.75, textTransform: 'none' }} />
          <Tab label="Care" sx={{ minHeight: 40, py: 0.75, textTransform: 'none' }} />
        </Tabs>

        {/* Contact List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </Box>
          )}

          {!isLoading && filteredContacts.length === 0 && (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {searchQuery ? 'No contacts found' : 'No contacts yet'}
              </Typography>
            </Box>
          )}

          {!isLoading && filteredContacts.length > 0 && (
            <List sx={{ p: 0.75 }}>
              {filteredContacts.map((contact, index) => (
                <ListItem
                  key={contact.con_id}
                  button
                  selected={selectedContact?.con_id === contact.con_id}
                  onClick={() => {
                    setSelectedContact(contact);
                    setIsEdit(false);
                  }}
                  sx={{
                    mb: 0.5,
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    '&.Mui-selected': {
                      backgroundColor: '#e3f2fd',
                      borderLeft: '3px solid #1976d2',
                      borderColor: '#1976d2',
                    },
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    px: 1.25,
                    py: 1,
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 48 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: getTypeColor(contact.con_contact_type),
                        width: 40,
                        height: 40,
                        fontSize: '1rem',
                      }}
                    >
                      {contact.con_first_name?.[0]}{contact.con_last_name?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {contact.con_first_name} {contact.con_last_name}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        {contact.con_designation || 'No designation'}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 96, ml: 'auto', textAlign: 'right' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {contact.con_status && (
                          <Chip 
                            label={contact.con_status} 
                            size="small" 
                            color={getBadgeColor(contact.con_status)}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        {contact.con_contact_type && (
                          <Chip 
                            label={contact.con_contact_type} 
                            size="small"
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem',
                              backgroundColor: getTypeColor(contact.con_contact_type),
                              color: 'white',
                            }}
                          />
                        )}
                      </Box>
                      {contact.con_last_contact_date && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary', textAlign: 'right' }}>
                          Last: {moment(contact.con_last_contact_date).format('MM/DD/YYYY')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </div>

      {/* RIGHT PANE - Contact Details */}
      <div style={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        backgroundColor: 'white',
      }}>
        {!selectedContact && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center',
            height: '100%',
            p: 4 
          }}>
            <img src='./Contacts.png' alt='Contacts' style={{ width: '10rem', height: '10rem', margin: 10, opacity: 0.5 }} />
            <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
              No Contact Selected
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
              Select a contact from the list to view details, or create a new contact to get started.
            </Typography>
          </Box>
        )}

        {selectedContact && !isEdit && (
          <Box>
            {/* Quick Actions Header */}
            <Box sx={{ 
              p: 3, 
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#fafafa'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: getTypeColor(selectedContact.con_contact_type),
                      width: 56,
                      height: 56,
                      fontSize: '1.5rem',
                      fontWeight: 600
                    }}
                  >
                    {selectedContact.con_first_name?.[0]}{selectedContact.con_last_name?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {selectedContact.con_first_name} {selectedContact.con_last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedContact.con_designation || 'No designation'}
                    </Typography>
                  </Box>
                </Box>
                <IconButton 
                  size="small"
                  onClick={() => setSelectedContact(null)}
                  sx={{ color: 'text.secondary' }}
                >
                  <HighlightOffIcon />
                </IconButton>
              </Box>

              {/* Quick Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip title={!hasSelectedContactPhone ? 'No phone number available' : ''}>
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<PhoneIcon />}
                      onClick={handleCall}
                      disabled={!hasSelectedContactPhone}
                      sx={{ textTransform: 'none' }}
                    >
                      Call
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={!hasSelectedContactEmail ? 'No email address available' : ''}>
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={handleEmail}
                      disabled={!hasSelectedContactEmail}
                      sx={{ textTransform: 'none' }}
                    >
                      Email
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={onEdit}
                  sx={{ textTransform: 'none' }}
                >
                  Edit
                </Button>
              </Box>
            </Box>

            {/* Contact Info Section */}
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Contact Info
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  {selectedContact.con_phone && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Phone
                      </Typography>
                      <Typography variant="body2">
                        {selectedContact.con_phone.split(',')[0]}
                      </Typography>
                    </Box>
                  )}
                  {selectedContact.con_email && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Email
                      </Typography>
                      <Typography variant="body2">
                        {selectedContact.con_email}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedContact.con_status && (
                      <Chip 
                        label={selectedContact.con_status} 
                        color={getBadgeColor(selectedContact.con_status)}
                      />
                    )}
                    {selectedContact.con_contact_type && (
                      <Chip 
                        label={selectedContact.con_contact_type}
                        sx={{ 
                          backgroundColor: getTypeColor(selectedContact.con_contact_type),
                          color: 'white',
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  {(selectedContact.con_Addr_line1 || selectedContact.con_city) && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Address
                      </Typography>
                      <Typography variant="body2">
                        {selectedContact.con_Addr_line1}
                        {selectedContact.con_Addr_line2 && <>, {selectedContact.con_Addr_line2}</>}
                        {selectedContact.con_city && (
                          <>
                            <br />
                            {selectedContact.con_city}, {selectedContact.con_state} {selectedContact.con_zip}
                          </>
                        )}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Case Relationship & History
              </Typography>
              <Box sx={{ display: 'grid', gap: 2 }}>
                {selectedContact.con_first_contact_date && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      First Contact
                    </Typography>
                    <Typography variant="body2">
                      {moment(selectedContact.con_first_contact_date).format('MM/DD/YYYY')}
                    </Typography>
                  </Box>
                )}
                {selectedContact.con_last_contact_date && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last Contact
                    </Typography>
                    <Typography variant="body2">
                      {moment(selectedContact.con_last_contact_date).format('MM/DD/YYYY')}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Full ConDetails component for additional functionality */}
              <ConDetails
                user={props.user}
                data={selectedContact}
                customerList={customerList}
                onDel={onDel}
                onEdit={onEdit}
                fetchData={fetchData}
              />
            </Box>
          </Box>
        )}

        {isEdit && selectedContact && (
          <Box>
            {/* Edit Header */}
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#fafafa'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Edit Contact: {selectedContact.con_first_name} {selectedContact.con_last_name}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setIsEdit(false)}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
            </Box>
            <Box sx={{ p: 3 }}>
              <ContactForm
                edit
                comp_id={props.comp_id}
                onUpdate={updateContact}
                onCancel={() => setIsEdit(false)}
                data={selectedContact}
              />
            </Box>
          </Box>
        )}
      </div>

      {redirectToNew && <Redirect to="/new" />}
    </div>
  );
};
